import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import {
    LocalDataTrack,
    RemoteParticipant,
    // createLocalTracks,
    // LocalVideoTrackPublication,
    LocalVideoTrack,
    Room as TwilioRoom,
    TwilioError,
} from 'twilio-video';
import { v4 as uuid } from 'uuid';
import { CircularProgress } from '@mui/material';
import { Call } from '@twilio/voice-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { isChrome } from 'src/utils/browser.util';
import { isNull } from 'lodash';
import EventTracker from 'src/pendo/EventTracker';
import withAuth from '../hoc/withAuth';
import {
    useRoom,
    useParticipants,
    // useVideoToggle,
    useTwilioUserMediaContext,
    useVoice,
    useVoiceAudioToggle,
    useAudioToggle,
    useCall,
} from '../customer-call-sdk';
import CallControlPanel from '../components/CallControlPanel';
import RemoteParticipantVideo from '../components/RemoteParticipantVideo';
import TextChat from '../components/TextChat';
import { TextChatMessage } from '../types';
import { toTextChatMessages } from '../utils/chat-history.util';
import { SWITCH_REJOIN_LIMIT, isOtherPartySwitching } from '../services/device-switch.service';
import { localTrackToMediaStream } from '../utils/local-track.util';
import useVideoTrack from '../hooks/useVideoTrack';
import useCallInfoName from '../hooks/useCallInfoName';
import useParticipantType from '../hooks/useParticipantType';
import useParseMeetingDetails from '../hooks/useParseMeetingDetails';
import { DU_MEETING_HANDOFF_ATTEMPT, buildPendoEventData } from '../utils/meeting-details.util';
import { ParticipantType } from '../types/participant.type';
import { useToast } from '../context/ToastContext';
import useAudioTrack from '../hooks/useAudioTrack';
import { muteVoiceConference, unmuteVoiceConference } from '../services/voice-conference.service';
import useCountdown from '../hooks/useCountdown';
import { getCustomerNumbers } from '../store/customer/actions';
import useUser from '../hooks/useUser';
import { RootState } from '../store';
import VideoInput from '../components/VideoInput';
import PhoneRinging from '../components/common/Icons/PhoneRinging';
import { CustomerState } from '../store/customer/types';
import CameraDisabledIcon from '../components/common/Icons/CameraDisabled';
import { useIdentity } from '../customer-call-sdk/context/IdentityContext';

/** Twilio's ParticipantDuplicateIdentityError: this identity connected from another device. */
const PARTICIPANT_DUPLICATE_IDENTITY = 53205;

const allowedParticipantTypes: ParticipantType[] = [
    'assigned_interpreter',
    'transfer_interpreter',
    'voice_caller',
    'hoh_user',
];

const StyledRoom = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: #000000;
    display: flex;
    .caller-info {
        position: absolute;
        z-index: 999;
        top: 2.5rem;
        left: 2.5rem;
        color: #fff;
        font-size: 1.25rem;
        font-weight: 700;
    }
    .video-container {
        position: relative;
        width: 100%;
        height: 100%;
        flex-grow: 1;
    }

    .remote-video-container {
        width: 100%;
        height: calc(100% - 80px);
        display: flex;
        flex-direction: row;
        padding: 2rem;
    }

    .remote-participant {
        width: 100%;
        height: 100%;
    }

    .call-control-panel {
        position: absolute;
        z-index: 999;
        bottom: 2rem;
        left: 50%;
        transform: translate(-50%, 0);
    }
    .connecting-indicator {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }
    .connecting-label {
        font-size: 1.5rem;
        font-weight: 600;
        color: #fff;
        margin-top: 1rem;
    }
    .text-chat-container {
        width: 420px;
        height: 100%;
    }
`;

const LocalVideo = styled(VideoInput)`
    position: absolute;
    z-index: 999;
    width: 220px;
    height: 150px;
    border: 2px solid #fff;
    border-radius: 0.8rem;
    top: 2.5rem;
    right: 2.5rem;
    background-color: #000000;

    video {
        transform: rotateY(180deg);
    }
`;

const StyledCameraDisabledIcon = styled(CameraDisabledIcon)`
    position: absolute;
    z-index: 999;
    width: 220px;
    height: 150px;
    border: 2px solid #fff;
    border-radius: 0.8rem;
    top: 2.5rem;
    right: 2.5rem;
    background-color: #000000;
    color: azure;
    video {
        transform: rotateY(180deg);
    }
`;

export interface RoomProps {}

const Room: React.FC<RoomProps> = () => {
    const toast = useToast();
    const dispatch = useDispatch();
    const { customerNumbers, activeCustomerNumber } = useSelector<RootState, CustomerState>(
        (state) => state.customerDetail,
    );

    const customerName = useSelector<RootState, CustomerState>((state) => state.customerDetail);

    const user = useUser();
    const call = useCall();
    const {
        connected,
        twilioVideoToken,
        region,
        requestType,
        vco,
        requestId,
        twilioVoiceToken,
        roomName,
        isOutgoingCall,
        saveChatHistory,
        sendSwitchChatHistory,
        switchChatHistory,
        clearSwitchChatHistory,
        isDeviceSwitchJoin,
    } = call;
    const callerNameInfo = useCallInfoName();
    const { audioTracks, videoTracks, audioDeviceId } = useTwilioUserMediaContext();

    const videoTrack = useVideoTrack();
    const audioTrack = useAudioTrack();
    const [dataTrack] = useState(new LocalDataTrack());
    const switchRejoinTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const switchMuteSyncedRef = useRef(false);
    // const { isVideoEnabled, toggleVideoEnabled } = useVideoToggle(videoTrack);
    const { isAudioEnabled } = useAudioToggle(audioTrack);

    const { room, connecting, error } = useRoom({
        token: twilioVideoToken,
        region,
        videoTracks,
        audioTracks: twilioVoiceToken ? [] : audioTracks,
        dataTrack,
    });

    const { participants } = useParticipants({ room });
    const { participantTypeMap } = useParticipantType({ participants, requestId });
    const { remoteVideoFlag, setRemoteVideoFlag } = useIdentity();

    const { inspectOutgoingMessage } = useParseMeetingDetails({ requestId, requestType });

    const { device, call: twilioCall } = useVoice({
        token: twilioVoiceToken,
        audioConstraints: audioDeviceId ? { deviceId: audioDeviceId } : undefined,
        meetingOptions: {
            meetingid: roomName || '',
            appname: 'userapp',
        },
        muted: !vco, // set default vco state
    });

    const { muted, mute, unmute } = useVoiceAudioToggle(twilioCall);
    const [chatVisible, setChatVisible] = useState(false);
    const [chatHasFocus, setChatHasFocus] = useState(false);
    const [localVideoToggle, setLocalVideoToggle] = useState(false);
    const [messages, setMessages] = useState<TextChatMessage[]>([]);

    useEffect(() => {
        saveChatHistory(messages);
    }, [messages, saveChatHistory]);

    // Waits for the room: the local identity tells this user's own messages apart.
    useEffect(() => {
        if (!switchChatHistory.length || !room) {
            return;
        }

        const restored = toTextChatMessages(switchChatHistory, room.localParticipant.identity);
        clearSwitchChatHistory();

        setMessages((previousMessages) => [...restored, ...previousMessages]);
    }, [switchChatHistory, clearSwitchChatHistory, room]);
    const [isLoading, setIsLoading] = useState(false);

    // countdown for P2P to check if the callee answer the call in 40 seconds
    const { startTimer, stopTimer, countdown } = useCountdown(40);
    // speaker will be off by default
    const [volumeOff, setVolumeOff] = useState(false);

    const canDisplayRemoteParticipant = useCallback(
        (participant: RemoteParticipant) => {
            if (!participantTypeMap.has(participant.identity)) {
                return false;
            }
            const participantType = participantTypeMap.get(participant.identity) || 'unknown';
            return allowedParticipantTypes.includes(participantType);
        },
        [participantTypeMap],
    );

    // const createLocalVideoTracks = () => {
    //     const options = {
    //         audio: true,
    //         video: true,
    //     };
    //     const tracksRecord = createLocalTracks(options);
    //     const videoTrackRoom = tracksRecord.then((trackData) => {
    //         return trackData.filter((data) => {
    //             return data;
    //         });
    //     });
    //     return videoTrackRoom;
    // };

    const enableDisableVideo = () => {
        room?.localParticipant.videoTracks.forEach((publication) => {
            if (publication.track.isEnabled) {
                publication.track.disable();
                setLocalVideoToggle(true);
            } else {
                publication.track.enable();
                setLocalVideoToggle(false);
            }
        });
    };

    let savedVideoTrack: LocalVideoTrack;

    const unpublishLocalVideoTrack = useCallback(() => {
        room?.localParticipant.videoTracks.forEach((publication) => {
            savedVideoTrack = publication.track;
            room?.localParticipant.unpublishTrack(publication.track);
            setLocalVideoToggle(true);
        });
    }, [room]);

    const publishLocalVideoTrack = useCallback(() => {
        const videoToBePublished = savedVideoTrack;
        setTimeout(() => {
            if (videoToBePublished) {
                room?.localParticipant.publishTrack(videoToBePublished).then(() => {
                    setLocalVideoToggle(false);
                });
            }
        }, 500);
    }, [room]);

    const handleCameraToggle = () => {
        if (isChrome() && isNull(call.interpreterId)) {
            if (!localVideoToggle) {
                unpublishLocalVideoTrack();
            } else {
                publishLocalVideoTrack();
            }
        } else {
            enableDisableVideo();
        }
    };

    useEffect(() => {
        if (isChrome() && call.connected && remoteVideoFlag) {
            if (!localVideoToggle) {
                unpublishLocalVideoTrack();
            } else {
                publishLocalVideoTrack();
                setRemoteVideoFlag(false);
            }
        }
    }, [remoteVideoFlag, localVideoToggle]);

    // const handleCameraToggle = useCallback(() => {
    //     if (room) {
    //         room.localParticipant.videoTracks.forEach((video) => {
    //             if (video.track.isEnabled && video.kind === 'video') {
    //                 video.track.disable();
    //                 setLocalVideoToggle(true);
    //             } else {
    //                 video.track.enable();
    //                 setLocalVideoToggle(false);
    //             }
    //         });
    //     }
    // }, [room]);

    const toggleVideoRoomAudio = useCallback(() => {
        if (room) {
            room.localParticipant.audioTracks.forEach((publication) => {
                if (publication.track.isEnabled) {
                    publication.track.disable();
                    call.setVco(false);
                } else {
                    publication.track.enable();
                    call.setVco(true);
                }
            });
        }
    }, [room]);

    const toggleVoiceRoomAudio = useCallback(() => {
        if (!roomName) {
            return;
        }
        if (muted) {
            unmute();
            call.setVco(true);
            unmuteVoiceConference({ roomname: roomName });
        } else {
            mute();
            call.setVco(false);
            muteVoiceConference({ roomname: roomName });
        }
    }, [roomName, muted, mute, unmute, call.setVco]);

    const syncVoiceRoomAudioState = useCallback(() => {
        if (!roomName || !twilioVoiceToken) {
            return;
        }

        if (muted) {
            muteVoiceConference({ roomname: roomName });
        } else {
            unmuteVoiceConference({ roomname: roomName });
        }
    }, [roomName, twilioVoiceToken, muted]);

    const handleMicrophoneToggle = useCallback(() => {
        if (twilioVoiceToken) {
            toggleVoiceRoomAudio();
        } else {
            toggleVideoRoomAudio();
        }
    }, [twilioVoiceToken, toggleVoiceRoomAudio, toggleVideoRoomAudio]);

    const handleChatToggle = useCallback(() => {
        setChatVisible((_chatVisible) => !_chatVisible);
    }, []);

    const refreshBalance = useCallback(
        (timeout: number) => {
            if (user) {
                const timerId = setTimeout(() => {
                    clearTimeout(timerId);
                    dispatch(getCustomerNumbers(user.uuid));
                }, timeout);
            }
        },
        [user],
    );

    const handleDisconnect = useCallback(
        (_room?: TwilioRoom, disconnectError?: TwilioError) => {
            // The same identity joined from another device: this device was switched away from.
            if (disconnectError?.code === PARTICIPANT_DUPLICATE_IDENTITY && requestId) {
                sendSwitchChatHistory(requestId);
            }
            stopTimer();
            if (room) {
                room.localParticipant.videoTracks.forEach((video) => {
                    if (!video.track.isEnabled) {
                        video.track.enable();
                    }
                });
            }
            if (device) {
                device.disconnectAll();
                device.destroy();
            }
            call.disconnect();
            if (user && activeCustomerNumber) {
                EventTracker.setAccountData(
                    user,
                    activeCustomerNumber,
                    customerNumbers.length,
                    null,
                );
            }
            refreshBalance(2000);
        },
        [
            room,
            device,
            stopTimer,
            call.disconnect,
            refreshBalance,
            requestId,
            sendSwitchChatHistory,
        ],
    );

    const handleHangup = useCallback(async () => {
        setIsLoading(true);
        stopTimer();
        if (room) {
            room.localParticipant.videoTracks.forEach((video) => {
                if (!video.track.isEnabled) {
                    video.track.enable();
                }
            });
        }

        if (room) {
            room.disconnect();
        }

        if (device) {
            device.disconnectAll();
            device.destroy();
        }
        await call.hangup();

        // workaround for VRS call when call is ended by the user
        // and the interpreter is still in the call try refresh after 2 mins
        if (requestType === 'VRS_OUTBOUND' || requestType === 'VRS_INBOUND') {
            refreshBalance(120 * 1000);
        }
        setIsLoading(false);
        // dispatch(getCustomerName(''));
    }, [
        stopTimer,
        room,
        device,
        call.hangup,
        requestType,
        refreshBalance,
        user,
        activeCustomerNumber,
        customerNumbers,
    ]);

    const handleSendMessage = useCallback(
        (text: string) => {
            if (dataTrack) {
                dataTrack.send(text);
                const message: TextChatMessage = {
                    id: uuid(),
                    text,
                    isRead: true,
                    type: 'outgoing',
                    from: room?.localParticipant.identity,
                    ts: Date.now(),
                };
                setMessages((_messages) => [..._messages, message]);

                inspectOutgoingMessage(text).then((details) => {
                    if (details && details.sent) {
                        EventTracker.trackEvent(
                            DU_MEETING_HANDOFF_ATTEMPT,
                            buildPendoEventData(details),
                        );
                    }
                });
            }
        },
        [dataTrack, inspectOutgoingMessage, room],
    );

    const handleIncomingMessage = useCallback((text: string, from: string) => {
        const message: TextChatMessage = {
            id: uuid(),
            text,
            isRead: true,
            type: 'incoming',
            from,
            ts: Date.now(),
        };
        setMessages((_messages) => [..._messages, message]);
    }, []);

    const handleParticipantConnected = useCallback(() => {
        stopTimer();
        syncVoiceRoomAudioState();
    }, [stopTimer, syncVoiceRoomAudioState]);

    const endIfStillEmpty = useCallback(() => {
        if (room && room.participants.size === 0 && room.state !== 'disconnected') {
            room.disconnect();
        }
    }, [room]);

    const handleParticipantDisconnected = useCallback(
        async (participant: RemoteParticipant) => {
            if (!room || room.participants.size > 0) {
                return;
            }

            // The interpreter stays in VRI and VRS, so an empty room is the end of the call.
            if (!requestType?.startsWith('P2P')) {
                room.disconnect();

                return;
            }

            // A device switch evicts the old device, which Twilio reports exactly like a hangup.
            const switching = await isOtherPartySwitching(requestId ?? '', participant.identity);

            // The lookup took time: the new device may have arrived, or this room may be gone.
            if (room.participants.size > 0 || room.state === 'disconnected') {
                return;
            }

            if (!switching) {
                room.disconnect();

                return;
            }

            clearTimeout(switchRejoinTimerRef.current);
            switchRejoinTimerRef.current = setTimeout(endIfStillEmpty, SWITCH_REJOIN_LIMIT);
        },
        [room, requestType, requestId, endIfStillEmpty],
    );

    useEffect(
        () => () => {
            clearTimeout(switchRejoinTimerRef.current);
        },
        [],
    );

    const sendDTMF = useCallback(
        (digit: string) => {
            if (digit.match(/[0-9*#]/)) {
                twilioCall?.sendDigits(digit);
            }
        },
        [twilioCall],
    );

    const handleKeyUp = useCallback(
        (event: KeyboardEvent) => {
            sendDTMF(event.key);
        },
        [sendDTMF],
    );

    const handleChatFocus = useCallback((focused: boolean) => {
        setChatHasFocus(focused);
    }, []);

    // Listen participant connet and disconnect
    useEffect(() => {
        room?.once('disconnected', handleDisconnect);
        room?.on('participantConnected', handleParticipantConnected);
        room?.on('participantDisconnected', handleParticipantDisconnected);

        return () => {
            room?.off('disconnected', handleDisconnect);
            room?.off('participantConnected', handleParticipantConnected);
            room?.off('participantDisconnected', handleParticipantDisconnected);
        };
    }, [room, handleDisconnect, handleParticipantConnected, handleParticipantDisconnected]);

    // Toggle audio when call is not a VRS outbound call. During other call twilioVoiceToken will be null
    useEffect(() => {
        if (!twilioVoiceToken && room) {
            room.localParticipant.audioTracks.forEach((publication) => {
                if (vco) {
                    publication.track.enable();
                } else {
                    publication.track.disable();
                }
            });
        }
    }, [twilioVoiceToken, room, vco]);

    // Toggle audio when it is a VRS outbound call and interpreter controls the user audio
    useEffect(() => {
        if (twilioCall?.status() === Call.State.Open) {
            if (vco) {
                unmute();
            } else {
                mute();
            }
        }
    }, [twilioCall, vco]);

    useEffect(() => {
        if (!isDeviceSwitchJoin || !twilioCall || !roomName || switchMuteSyncedRef.current) {
            return;
        }

        const muteAfterSwitch = () => {
            switchMuteSyncedRef.current = true;
            mute();
            call.setVco(false);
            muteVoiceConference({ roomname: roomName });
        };

        if (twilioCall.status() === Call.State.Open) {
            muteAfterSwitch();
            return;
        }

        twilioCall.once('accept', muteAfterSwitch);

        return () => {
            twilioCall.off('accept', muteAfterSwitch);
        };
    }, [isDeviceSwitchJoin, twilioCall, roomName, mute, call.setVco]);

    // listen for call error and show error then disconnect the call
    useEffect(() => {
        if (error) {
            toast.error(error);
            call.disconnect();
        }
    }, [error, toast.error, call.disconnect]);

    // Listen key press event when chat is not focused to send DTMF
    useEffect(() => {
        if (!chatHasFocus && twilioCall) {
            document.addEventListener('keyup', handleKeyUp);
        }
        return () => {
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [twilioCall, chatHasFocus]);

    // Listen for P2P call and start countdown timer to check callee answers call in 40 seconds
    useEffect(() => {
        if (room && requestType === 'P2P' && isOutgoingCall) {
            startTimer();
        }
    }, [room, requestType, isOutgoingCall, startTimer]);

    // Check for downdown time equals 0 and no callee joins the P2P call then disconnects the call
    useEffect(() => {
        if (requestType === 'P2P' && countdown === 0 && room?.participants.size === 0) {
            toast.error('The number you have called is not answering, please try again later.');
            handleHangup();
        }
    }, [requestType, countdown, handleHangup]);

    if (!connected) {
        return <Navigate to="/home" replace />;
    }

    const customerFlag =
        customerName.customerName !== undefined && customerName.customerName !== ''
            ? customerName.customerName
            : callerNameInfo;

    return (
        <StyledRoom>
            {room && (
                <>
                    <div className="text-chat-container">
                        <TextChat
                            chatVisible
                            messages={messages}
                            onSendMessage={handleSendMessage}
                            callRequestType={requestType}
                            onFocus={handleChatFocus}
                        />
                    </div>
                    <div className="video-container">
                        {customerFlag && (
                            <div className="caller-info">
                                <PhoneRinging />
                                <span className="ml-2">{customerFlag}</span>
                            </div>
                        )}
                        <>
                            {localVideoToggle ? (
                                <StyledCameraDisabledIcon />
                            ) : (
                                videoTrack && (
                                    <LocalVideo
                                        mediaStream={localTrackToMediaStream(videoTrack)}
                                        className="local-video-container"
                                        resize
                                    />
                                )
                            )}
                        </>

                        <div className="remote-video-container">
                            {participants.map((participant) => (
                                <RemoteParticipantVideo
                                    className="remote-participant"
                                    key={participant.sid}
                                    participant={participant}
                                    onMessage={handleIncomingMessage}
                                    isVisible={canDisplayRemoteParticipant(participant)}
                                    enableAudio={!twilioVoiceToken}
                                    volumeOff={volumeOff}
                                />
                            ))}
                        </div>
                        <CallControlPanel
                            callType="P2P"
                            isConnecting={false}
                            className="call-control-panel"
                            onHangup={handleHangup}
                            onMicrophoneToggle={handleMicrophoneToggle}
                            microphoneDisabled={twilioVoiceToken ? muted : !isAudioEnabled}
                            onCameraToggle={handleCameraToggle}
                            cameraDisabled={localVideoToggle}
                            chatVisible={chatVisible}
                            onChatToggle={handleChatToggle}
                            volumeOff={volumeOff}
                            onVolumeToggle={() => setVolumeOff(!volumeOff)}
                            endDisabled={isLoading}
                        />
                    </div>
                </>
            )}
            {connecting && (
                <div className="connecting-indicator">
                    <CircularProgress size={60} />
                    <div className="connecting-label">Connecting to the room</div>
                </div>
            )}
        </StyledRoom>
    );
};

export default withAuth(Room);
